import { useEffect, useMemo } from 'react'

import { useGetRevenueAndDownloadAggregatesQuery } from '../../../api/core'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { SearchGamesQueryParams } from '../../game-search/types/SearchGamesQueryParams'
import { Game } from '../types/Game'
import { useGameSearch } from './gameHooks'

const gameIncludes = [
  'name',
  'names',
  'icon',
  'icons',
  'artist',
  'gpsPerMarket',
  'genreId',
  'subGenreId',
  'appId',
  'owner',
  'tags',
  'conventionalSubgenreId',
  'conventionalSubgenre',
  'archive',
  'reviewId',
  'reviewPublished',
]

const useGetOtherGamesWithTag = (conventionalSubgenreId: string, marketIso: string, tagId: string, skip: boolean = false) => {
  const userLanguage = useCurrentUserLanguage()
  const {
    searchGames,
    result: { data: searchedGames, isLoading: isFetchingOtherGamesWithTag },
  } = useGameSearch()

  useEffect(() => {
    if (!skip) {
      const searchGamesParams: Omit<SearchGamesQueryParams, 'userLanguage'> = {
        term: '',
        marketIso: marketIso,
        include: gameIncludes.join(','),
        owned: false,
        tags: tagId,
        conventionalSubgenreIds: conventionalSubgenreId,
      }

      searchGames(searchGamesParams)
    }
  }, [searchGames, conventionalSubgenreId, tagId, marketIso, skip])

  let otherGamesWithTag: Game[] | undefined

  if (searchedGames) {
    otherGamesWithTag = searchedGames.filter((game) => {
      return game.gpsPerMarket[marketIso] > 0
    })
  }

  const marketIsos = useMemo(() => {
    return [marketIso]
  }, [marketIso])

  let otherGamesWithTagAppIds
  if (otherGamesWithTag) {
    otherGamesWithTagAppIds = otherGamesWithTag.map((game) => {
      return game.appId
    })
  }

  const { data: revenueAndDownloadByApp, isFetching: isFetchingRevenueAndDownloadByApp } = useGetRevenueAndDownloadAggregatesQuery(
    { appIds: otherGamesWithTagAppIds ? otherGamesWithTagAppIds : [], marketIsos },
    { skip: skip && isFetchingOtherGamesWithTag }
  )

  const otherSubgenreGamesRevenueAndDownloadAggregates = useMemo(() => {
    if (!otherGamesWithTag || !revenueAndDownloadByApp) {
      return []
    }

    return otherGamesWithTag
      .map((game) => {
        const appAggregates = revenueAndDownloadByApp && revenueAndDownloadByApp[game.appId]
        return new Game(
          {
            ...game,
            revenueAndDownloadAggregatesMap: appAggregates?.countryAggregates || {},
          } as any,
          { userLanguage }
        )
      })
      .sort((a, b) => (a.resolvedName < b.resolvedName ? -1 : 1))
  }, [otherGamesWithTag, revenueAndDownloadByApp, userLanguage])

  return {
    data: otherSubgenreGamesRevenueAndDownloadAggregates,
    isFetching: isFetchingOtherGamesWithTag || isFetchingRevenueAndDownloadByApp,
  }
}

export default useGetOtherGamesWithTag
